import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(n) {
  return (Number(n) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export default function HomeownerQuoteView() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`)
      .then((r) => {
        setQuote(r.data);
        if (r.data.status === "accepted") {
          setAccepted(true);
          setAcceptedAt(r.data.accepted_at);
        }
      })
      .catch((e) => setErr(e.response?.data?.detail || "This quote is not available."))
      .finally(() => setLoading(false));
  }, [token]);

  async function acceptQuote() {
    if (accepting || accepted) return;
    setAccepting(true);
    try {
      const r = await axios.post(`${API}/public/quotes/${token}/accept`);
      setAccepted(true);
      setAcceptedAt(r.data.accepted_at || new Date().toISOString());
      setConfirm(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Unable to accept the quote right now.");
    } finally {
      setAccepting(false);
    }
  }

  if (loading) return <div className="p-8 text-brand-navy text-center">Loading quote…</div>;
  if (err || !quote) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6" data-testid="homeowner-quote-notfound">
        <div className="bg-white border border-brand-border rounded-lg p-8 max-w-md text-center">
          <img src="/branding/primary-logo.png" alt="ColourMatch" className="h-10 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-brand-navy mb-2">Quote not available</h1>
          <p className="text-sm text-gray-600">{err || "This link is no longer valid."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream py-8 px-4" data-testid="homeowner-quote-view">
      <div className="max-w-3xl mx-auto bg-white border border-brand-border rounded-lg shadow-lg p-8 sm:p-10">
        <div className="flex items-start justify-between mb-6 pb-6 border-b border-brand-border">
          <div>
            <img src="/branding/primary-logo.png" alt="ColourMatch" className="h-10 mb-3" />
            <div className="text-xs uppercase tracking-widest text-brand-teal font-bold">Quote</div>
            <h1 className="text-3xl font-bold text-brand-navy" data-testid="hq-number">{quote.quote_number}</h1>
          </div>
          <div className="text-right text-sm text-gray-700">
            <div className="text-xs text-gray-500 uppercase tracking-widest">Date</div>
            <div className="font-semibold" data-testid="hq-date">{new Date(quote.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">From</div>
            <div className="text-brand-navy font-semibold" data-testid="hq-from-name">{quote.painter_business.business_name || "—"}</div>
            {quote.painter_business.abn && <div className="text-sm text-gray-700">ABN {quote.painter_business.abn}</div>}
            <div className="text-sm text-gray-700">{quote.painter_business.contact_email}</div>
            {quote.painter_business.contact_phone && <div className="text-sm text-gray-700">{quote.painter_business.contact_phone}</div>}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">Prepared for</div>
            <div className="text-brand-navy font-semibold">{quote.homeowner.name || "—"}</div>
            <div className="text-sm text-gray-700">{quote.homeowner.email}</div>
            {quote.homeowner.address && <div className="text-sm text-gray-700 mt-1">{quote.homeowner.address}</div>}
          </div>
        </div>

        <table className="w-full text-sm mb-6" data-testid="hq-items-table">
          <thead className="border-b-2 border-brand-navy text-brand-navy">
            <tr>
              <th className="text-left py-2">Description</th>
              <th className="text-right py-2 w-16">Qty</th>
              <th className="text-right py-2 w-24">Unit</th>
              <th className="text-right py-2 w-28">Line total</th>
            </tr>
          </thead>
          <tbody>
            {quote.line_items.map((i) => (
              <tr key={i.id} className="border-b border-brand-border">
                <td className="py-2 pr-2">{i.description}</td>
                <td className="py-2 text-right">{i.quantity}</td>
                <td className="py-2 text-right">{money(i.unit_price)}</td>
                <td className="py-2 text-right font-semibold">{money(i.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={3} className="py-2 text-right">Subtotal</td><td className="py-2 text-right font-semibold" data-testid="hq-subtotal">{money(quote.subtotal)}</td></tr>
            <tr><td colSpan={3} className="py-2 text-right">GST (10%)</td><td className="py-2 text-right" data-testid="hq-gst">{money(quote.gst)}</td></tr>
            <tr className="border-t-2 border-brand-navy text-brand-navy"><td colSpan={3} className="py-3 text-right text-lg font-bold">Total (incl. GST)</td><td className="py-3 text-right text-lg font-bold" data-testid="hq-total">{money(quote.total)}</td></tr>
          </tfoot>
        </table>

        {quote.notes && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-1">Notes</div>
            <p className="text-sm text-gray-800 whitespace-pre-line">{quote.notes}</p>
          </div>
        )}
        {quote.terms && (
          <div className="mb-6 border-t border-brand-border pt-4">
            <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-1">Terms &amp; conditions</div>
            <p className="text-sm text-gray-700 whitespace-pre-line">{quote.terms}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-brand-border">
          <a href={`${API}/public/quotes/${token}/pdf`} target="_blank" rel="noreferrer"
             className="px-4 py-2 rounded-lg border border-brand-navy text-brand-navy font-semibold text-sm hover:bg-brand-cream"
             data-testid="hq-download-pdf">Download PDF</a>

          {accepted ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-success/10 text-brand-success font-semibold text-sm" data-testid="hq-accepted-banner">
              ✓ Accepted{acceptedAt ? ` on ${new Date(acceptedAt).toLocaleDateString("en-AU")}` : ""}
            </div>
          ) : confirm ? (
            <div className="flex items-center gap-2">
              <button onClick={acceptQuote} disabled={accepting}
                className="px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm hover:bg-brand-navy/90 disabled:opacity-50"
                data-testid="hq-confirm-accept">
                {accepting ? "Accepting…" : "Yes, accept quote"}
              </button>
              <button onClick={() => setConfirm(false)} className="px-4 py-2 rounded-lg text-brand-navy text-sm">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirm(true)}
              className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90"
              data-testid="hq-accept-btn">
              Accept this quote
            </button>
          )}
        </div>

        <p className="text-xs text-gray-500 text-center mt-8 pt-6 border-t border-brand-border">
          Powered by ColourMatch · colourmatch.au
        </p>
      </div>
    </div>
  );
}
